import { parseISO } from 'date-fns';
import { rangeWindowSize } from 'utils/dates';
import { MONTHLY } from './frequency';
const DAY_PADDING = 3;
const HEADROOM = 0.15;
const suggestionStep = (value) => (value > 100 ? 10 : 5);
const suggestMaxAmount = (value) => {
    const step = suggestionStep(value);
    return Math.ceil((value * (1 + HEADROOM)) / step) * step;
};
const suggestMinAmount = (value) => {
    const step = suggestionStep(value);
    const suggested = Math.floor((value * (1 - HEADROOM)) / step) * step;
    return suggested > 0 ? suggested : null;
};
/**
 * A min/max month-day pair is a cyclic window: min > max wraps around the
 * month boundary (e.g. 28th -> 2nd). Mirrors the backend's
 * transactionMeetsUserCriteria.
 */
const dayIsInRange = (day, min, max) => min <= max ? day >= min && day <= max : day >= min || day <= max;
// The canonical "any day of the month" range, as persisted by the
// adjust-rules screen.
const ANY_DAY = { minMonthDay: 1, maxMonthDay: 31 };
const suggestMonthDayRange = (day, min, max) => {
    // Extend a few days past the payment so the bound isn't razor-thin,
    // wrapping around the month boundary when the padding runs past it. When
    // even the wrap can't fit — it would lap the opposite bound, leaving a
    // window of (nearly) the whole month — suggest "any day" instead.
    if (min <= max) {
        // Plain range: extend whichever side the day fell out of.
        if (day > max) {
            const target = day + DAY_PADDING;
            if (target <= 31)
                return { minMonthDay: min, maxMonthDay: target };
            const wrapped = target - 31;
            return wrapped >= min - 1
                ? ANY_DAY
                : { minMonthDay: min, maxMonthDay: wrapped };
        }
        const target = day - DAY_PADDING;
        if (target >= 1)
            return { minMonthDay: target, maxMonthDay: max };
        const wrapped = target + 31;
        return wrapped <= max + 1
            ? ANY_DAY
            : { minMonthDay: wrapped, maxMonthDay: max };
    }
    // Wrapped range (e.g. 28th -> 2nd): the day sits in the gap between max
    // and min. Extend whichever side yields the smaller overall window.
    const paddedMax = Math.min(day + DAY_PADDING, min - 1);
    const paddedMin = Math.max(day - DAY_PADDING, max + 1);
    const extendMax = { minMonthDay: min, maxMonthDay: paddedMax };
    const extendMin = { minMonthDay: paddedMin, maxMonthDay: max };
    const best = rangeWindowSize(min, paddedMax) <= rangeWindowSize(paddedMin, max)
        ? extendMax
        : extendMin;
    return rangeWindowSize(best.minMonthDay, best.maxMonthDay) === 31
        ? ANY_DAY
        : best;
};
/**
 * Compares a transaction against the subscription's user matching rules
 * (amount range and month-day range — the same checks the backend's
 * transactionMeetsUserCriteria applies) and suggests widened rules that
 * would have matched it. Returns null when the transaction already fits or
 * no rules are set.
 *
 * Suggested amounts sit 15% past the payment, rounded away from it to a
 * tidy step — £5 for payments up to £100, £10 above — so the headroom
 * scales with the amount and the snap never thins it (a £10 payment above
 * the max suggests £15; a £135.50 one suggests £160). A min suggestion
 * that reaches zero comes back as null, meaning "remove the minimum".
 * Month-day bounds get three days of padding past the day the payment
 * arrived, wrapping around the month boundary when needed; when the
 * padding would lap the opposite bound the suggestion falls back to the
 * full 1st–31st, i.e. any day.
 *
 * The predicted date and frequency need no suggestion here: adding the
 * transaction makes the backend recompute both from the charge history.
 */
export const getMatchingRuleSuggestions = (subscription, transaction) => {
    const { minAmount, maxAmount, minMonthDay, maxMonthDay, paymentFrequency } = subscription;
    const actualAmount = Math.abs(transaction.nativeAmount?.amount ?? transaction.amount);
    let amount = null;
    // Truthy checks mirror the backend: a bound of 0/null is not enforced.
    if (minAmount && actualAmount < minAmount) {
        amount = {
            actualAmount,
            direction: 'below',
            current: { minAmount, maxAmount: maxAmount ?? null },
            suggested: {
                minAmount: suggestMinAmount(actualAmount),
                maxAmount: maxAmount ?? null,
            },
        };
    }
    else if (maxAmount && actualAmount > maxAmount) {
        amount = {
            actualAmount,
            direction: 'above',
            current: { minAmount: minAmount ?? null, maxAmount },
            suggested: {
                minAmount: minAmount ?? null,
                maxAmount: suggestMaxAmount(actualAmount),
            },
        };
    }
    let monthDay = null;
    // The day-range rule is a monthly-subscription concept: the adjust-rules
    // screen only offers it when the frequency is monthly, so only suggest
    // updating it for monthly subscriptions. The backend additionally only
    // enforces the window when both bounds are set.
    if (paymentFrequency === MONTHLY && minMonthDay && maxMonthDay) {
        const actualDay = parseISO(transaction.customDate ?? transaction.bookingDate).getDate();
        if (!dayIsInRange(actualDay, minMonthDay, maxMonthDay)) {
            monthDay = {
                actualDay,
                current: { minMonthDay, maxMonthDay },
                suggested: suggestMonthDayRange(actualDay, minMonthDay, maxMonthDay),
            };
        }
    }
    if (!amount && !monthDay)
        return null;
    return { amount, monthDay };
};
